import Phaser from "phaser"
import { calculateScaleFactor } from "../config/scaling"
import {
	createTopBar,
	checkLocalSavedData,
	handleGameProgressUpdate,
	updateMiniLevel,
	displayInstructions,
	displayTooltip,
	destroyTooltip,
	displayIndicator,
	destroyIndicator,
	playUISound,
	saveAlertsOpenedLocally,
	playErrorAnimation,
	playNotificationAnimation,
	displayTutorialHand,
	playExplosionAnimation,
} from "../helpers/levelManager"

export default class AlertsScene extends Phaser.Scene {
	constructor() {
		super({ key: "Alerts" })
		this.alertContentGroup = null
		this.alertsGroup = null
		this.pageIndicators = []
	}

	init() {
		// Game dimensions and scaling
		this.gameW = this.cameras.main.width
		this.gameH = this.cameras.main.height

		// Get scaling information
		const scalingData = calculateScaleFactor(this)
		this.scaleFactor = scalingData.scaleFactor
		this.safeZoneX = scalingData.safeZoneX
		this.safeZoneY = scalingData.safeZoneY
		this.safeZoneWidth = scalingData.safeZoneWidth
		this.safeZoneHeight = scalingData.safeZoneHeight
		this.templateY = scalingData.templateY

		// Responsive font sizing
		this.baseFontSize = 20
		this.fontSize = this.baseFontSize * this.scaleFactor

		// Load alerts data from JSON
		this.emailsAndAlerts = this.cache.json.get("emailsAndAlerts")
		this.alerts = this.emailsAndAlerts.alerts

		this.loadSavedData()
	}

	loadSavedData() {
		this.savedUserData = checkLocalSavedData()
		this.savedLevel = this.savedUserData?.userLevel || 0
		this.savedMiniLevel = this.savedUserData?.userMiniLevel || 0
		this.currentPlanet = this.savedUserData?.currentPlanet || 1

		this.tabletScene = this.scene.get("TabletScene")
		this.cockpitScene = this.scene.get("CockpitScene")
	}

	create() {
		this.alertScene = this
		this.soundManager = this.scene.get("GameUI").soundManager
		//this.soundManager.addCategory('ambient', 0.5);
		//this.soundManager.playMusic("Muzac_bad_speaker", { volume: 0.2 })
		createTopBar(this)

		// Create main UI elements
		this.alertsBG = this.add
			.image(this.gameW / 2, this.gameH / 2, "tabletBG")
			.setScale(this.scaleFactor, this.scaleFactor * 1.08)

		this.appBar = this.add
			.image(
				this.gameW / 2,
				this.gameH / 2 - 125 * this.scaleFactor,
				"alertsAppBar",
			)
			.setScale(this.scaleFactor)
			.setDepth(15)

		this.appBarText = this.add
			.text(this.appBar.x, this.appBar.y, "Alerts", {
				fontFamily: "Gluten",
				fontSize: this.fontSize + 4 * this.scaleFactor,
				color: "white",
				align: "left",
				fontStyle: "bold",
				resolution: 2,
			})
			.setOrigin(0.5)
			.setDepth(15)

		// Create groups for better management
		this.alertsGroup = this.add.group()
		this.alertContentGroup = this.add.group()

		// Add close button
		this.closeButton = this.add
			.image(
				this.appBar.x + 230 * this.scaleFactor,
				this.appBar.y,
				"appCloseButton",
			)
			.setDepth(15)
			.setScale(this.scaleFactor * 0.5)

		this.closeButton.setInteractive().on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			this.scene.resume("TabletScene")
			//console.log("Close pressed")
			this.tabletScene.updateButtonVisibility()
			this.tabletScene.removeNotification()
			this.cockpitScene.removeNotification()
			this.tabletScene.removeUnlockAnim()
			if (this.currentPlanet === 2 && this.savedMiniLevel === 5) {
				displayInstructions(
					this.cockpitScene,
					"Press the left and right arrow buttons to look around your ship",
					14,
					500,
					60,
				)
			}
			// if (this.currentPlanet === 2 && this.savedMiniLevel === 13) {

			// 	this.cockpitScene.creature3Panel()
			// }
			this.scene.stop("Alerts")
			this.removeHtmlItems()
			if (this.wifiPasswordReference) {
				this.wifiPasswordReference.remove()
			}
		})

		//this.alertsGroup.add(this.closeButton)
		this.handleAlertCreation()
	}

	handleAlertCreation() {
		this.loadSavedData()
		if (this.currentPlanet === 1) {
			const alertsOpened = this.savedUserData?.alertsOpened || {}
			console.log("Alerts opened", alertsOpened)

			const alertConditions = {
				facialScan: this.savedMiniLevel >= 11,
			}

			const unopenedAlerts = Object.keys(alertConditions).filter(
				(alert) => !alertsOpened[alert] && alertConditions[alert],
			)

			if (unopenedAlerts.length === 0) {
				console.log("All alerts already opened")
				this.createNoNewAlerts()
			} else {
				console.log("Unopened alerts", unopenedAlerts)
				unopenedAlerts.forEach((alert, index) => this.createAlert(alert, index))
			}
		}
		if (this.currentPlanet === 2) {
			const alertsOpened = this.savedUserData?.alertsOpened || {}
			console.log("Alerts opened", alertsOpened)

			const alertConditions = {
				securityCheck: this.savedMiniLevel >= 5,
				restartDevice: this.savedMiniLevel >= 11,
			}

			const unopenedAlerts = Object.keys(alertConditions).filter(
				(alert) => !alertsOpened[alert] && alertConditions[alert],
			)

			if (unopenedAlerts.length === 0) {
				console.log("All alerts already opened")
				this.createNoNewAlerts()
			} else {
				console.log("Unopened alerts", unopenedAlerts)
				unopenedAlerts.forEach((alert, index) => this.createAlert(alert, index))
			}
		}
		if (this.currentPlanet === 3) {
			const alertsOpened = this.savedUserData?.alertsOpened || {}
			console.log("Alerts opened", alertsOpened)

			const alertConditions = {
				reconnectDevice: this.savedMiniLevel >= 1,
				robotAlert: this.savedMiniLevel >= 8,
				otpAlert: this.savedMiniLevel >= 16,
			}

			const unopenedAlerts = Object.keys(alertConditions).filter(
				(alert) => !alertsOpened[alert] && alertConditions[alert],
			)

			if (unopenedAlerts.length === 0) {
				console.log("All alerts already opened")
				this.createNoNewAlerts()
			} else {
				console.log("Unopened alerts", unopenedAlerts)
				unopenedAlerts.forEach((alert, index) => this.createAlert(alert, index))
			}
		}

		//DEBUG
		// this.createAlert("facialScan", 0)
		// this.createAlert("securityCheck", 1)
		// this.createAlert("restartDevice", 2)
		// this.createAlert("reconnectDevice", 3)
		// this.createAlert("robotAlert", 4)
		//this.createAlert("otpAlert", 5)
	}

	createNoNewAlerts() {
		const alertIcon = this.add.image(
			this.alertsBG.x,
			this.alertsBG.y,
			"alertIcon2",
		)
		alertIcon.setScale(0.5)

		const noNewAlertsText = this.add
			.text(
				this.alertsBG.x,
				this.alertsBG.y + 30 * this.scaleFactor,
				"\nNo new alerts right now.\nTry again later.",
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 8 * this.scaleFactor,
					color: "#11044E",
					align: "center",
					resolution: 2,
				},
			)
			.setOrigin(0.5)
	}

	createAlert(type, index) {
		console.log("Alert Index:", index, "Type:", type)

		// Fixed spacing between alerts
		const alertSpacing = 40 * this.scaleFactor

		// Calculate vertical position starting after appBar
		const baseY = this.appBar.y + 80 * this.scaleFactor // Start after appBar with some padding
		const alertY = baseY + index * alertSpacing

		let alertSubject = ""

		if (type === "facialScan") {
			alertSubject = "Log in with facial recognition"
		}

		if (type === "securityCheck") {
			alertSubject = "Security check"
		}

		if (type === "restartDevice") {
			alertSubject = "Device needs to be shut down and restarted."
		}

		if (type === "reconnectDevice") {
			alertSubject = "Reconnect your device!"
		}

		if (type === "robotAlert") {
			alertSubject = "Drag and drop"
		}

		if (type === "otpAlert") {
			alertSubject = "Enter an OTP (One-Time Password)"
		}

		const alertIcon = this.add.image(
			this.alertsBG.x - 230 * this.scaleFactor,
			alertY, // Use calculated Y position
			"alertIcon",
		)

		const alertTitle = this.add
			.text(
				this.alertsBG.x - 215 * this.scaleFactor,
				alertY - 9 * this.scaleFactor, // Adjust text position relative to icon
				alertSubject,
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 6 * this.scaleFactor,
					color: "#11044E",
					align: "left",
					fontStyle: "bold",
					resolution: 2,
				},
			)
			.setInteractive()

		alertTitle.on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			this.createAlertBody(type)
		})

		const alertButton = this.add
			.image(
				this.alertsBG.x + 200 * this.scaleFactor,
				alertY, // Use calculated Y position
				"saveBtn",
			)
			.setScale(this.scaleFactor * 1)
			.setInteractive()

		const alertText = this.add
			.text(alertButton.x, alertButton.y - 5 * this.scaleFactor, "OPEN", {
				fontFamily: "Gluten",
				fontSize: this.fontSize - 2 * this.scaleFactor,
				color: "white",
				align: "center",
				resolution: 2,
				fontWeight: "700",
				fontStyle: "bold",
				resolution: 2,
			})
			.setOrigin(0.5)

		alertButton.on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			this.createAlertBody(type)
		})

		// Add elements to the group for better management
		this.alertsGroup.add(alertIcon)
		this.alertsGroup.add(alertTitle)
		this.alertsGroup.add(alertButton)
		this.alertsGroup.add(alertText)
	}

	createAlertBody(type) {
		// Clear any existing content
		this.alertContentGroup.clear(true, true)

		let correctSelections = 0
		let requiredSelections = 3 // Number of correct selections needed
		let isDone = false

		// Hide the alerts list
		this.alertsGroup.setVisible(false)

		// Back button
		const backButton = this.add
			.image(
				this.appBar.x - 230 * this.scaleFactor,
				this.appBar.y,
				"appBackButton",
			)
			.setDepth(10)
			.setScale(this.scaleFactor * 0.5)

		backButton.setInteractive().on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			// Clean up event listeners
			this.input.off("pointerdown")
			this.input.off("pointermove")
			this.input.off("pointerup")

			// Clear content and show alerts list
			this.alertContentGroup.clear(true, true)
			this.alertsGroup.setVisible(true)
		})

		// Next button
		const nextButton = this.add
			.image(
				this.appBar.x + 200 * this.scaleFactor,
				this.appBar.y + 260 * this.scaleFactor,
				"saveBtn",
			)
			.setDepth(10)
			.setScale(this.scaleFactor)
			.setAlpha(0.35)

		const nextText = this.add
			.text(nextButton.x, nextButton.y - 10 * this.scaleFactor, "Next", {
				fontFamily: "Gluten",
				fontSize: this.fontSize,
				color: "white",
				align: "left",
				fontWeight: "700",
				fontStyle: "bold",
				resolution: 2,
			})
			.setDepth(10)
			.setOrigin(0.5)
			.setAlpha(0.35)

		// Create UI elements based on alert type
		if (type === "facialScan") {
			this.handleFacialScanAlert(backButton, nextButton, nextText, isDone)
		}

		if (type === "restartDevice") {
			this.handleRestartDeviceAlert(backButton, nextButton, nextText, isDone)
		}

		if (type === "securityCheck") {
			this.handleSecurityCheckAlerts(backButton, nextButton, nextText, isDone)
		}

		if (type === "reconnectDevice") {
			this.handleReconnectAlert(backButton, nextButton, nextText, isDone)
		}

		if (type === "robotAlert") {
			this.handleRobotAlert(backButton, nextButton, nextText, isDone)
		}

		if (type === "otpAlert") {
			this.handleOtpAlert(backButton, nextButton, nextText, isDone)
		}
	}

	handleFacialScanAlert(backButton, nextButton, nextText, isDone) {
		const blueAlertString =
			"Press the [b]TOGGLE BUTTON[/b]\non the right to turn on\nfacial login."
		const clearAlertString =
			"Your tablet can be logged\ninto by using your face"
		let togglePressed = false

		// Create alert holders
		const blueAlertHolder = this.add
			.image(
				this.alertsBG.x - 60 * this.scaleFactor,
				this.alertsBG.y + 50 * this.scaleFactor,
				"blueAlertContainerSmall",
			)
			.setScale(this.scaleFactor)
			.setDepth(10)

		const blueAlertText = this.add
			.rexBBCodeText(blueAlertHolder.x, blueAlertHolder.y, blueAlertString, {
				fontFamily: "Space Grotesk",
				fontSize: this.fontSize - 6 * this.scaleFactor,
				color: "white",
				align: "center",
				resolution: 2,
				fontWeight: "400",
			})
			.setOrigin(0.5)
			.setDepth(10)

		const clearAlertHolder = this.add
			.image(
				this.alertsBG.x - 60 * this.scaleFactor,
				this.alertsBG.y - 50 * this.scaleFactor,
				"clearAlertWarning",
			)
			.setScale(this.scaleFactor * 0.5)
			.setDepth(10)

		const clearAlertText = this.add
			.text(
				clearAlertHolder.x + 10 * this.scaleFactor,
				clearAlertHolder.y,
				clearAlertString,
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 6 * this.scaleFactor,
					color: "#11044E",
					align: "center",
					fontWeight: "400",
					resolution: 2,
				},
			)
			.setOrigin(0.5)
			.setDepth(10)

		// Create toggle button
		const toggleButton = this.add
			.image(
				this.alertsBG.x + 150 * this.scaleFactor,
				this.alertsBG.y,
				"alertsToggleOff",
			)
			.setScale(this.scaleFactor * 0.55)
			.setDepth(10)
			.setInteractive()

		displayTutorialHand(this, {
			xPos: toggleButton.x - 45 * this.scaleFactor,
			yPos: toggleButton.y + 60 * this.scaleFactor,
			rotation: 35,
		})

		toggleButton.on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			if (togglePressed) {
				toggleButton.setTexture("alertsToggleOff")
				togglePressed = false
				destroyTooltip(this)
				destroyIndicator(this)
				nextButton.input.enabled = false
				nextButton.setAlpha(0.5)
				nextText.setAlpha(0.5)
				return
			} else {
				toggleButton.setTexture("alertsToggleOn")
				togglePressed = true
				destroyTooltip(this)
				destroyIndicator(this)
				nextButton.input.enabled = true
				nextButton.setAlpha(1)
				nextText.setAlpha(1)
				return
			}
		})

		let yOffset
		let fontScale
		let wordWrap = 1.01
		if (isMobile) {
			yOffset = 3
			fontScale = 7
		} else {
			yOffset = 6
			fontScale = 9
			wordWrap = 1.01
		}

		// displayTooltip(this, {
		// 	xPos: toggleButton.x,
		// 	yPos: toggleButton.y + 50 * this.scaleFactor,
		// 	type: "pressMeTip",
		// 	scale: 1,
		// 	text: "press me!",
		// 	fontScale: -7,
		// 	xTextOffset: 1,
		// 	yTextOffset: yOffset,
		// 	wrapWidth: wordWrap,
		// })

		const isMobile =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent,
			)

		displayIndicator(
			this,
			toggleButton.x,
			toggleButton.y - 2.5 * this.scaleFactor,
			0.8,
			"foodSaveIndicator",
		)

		// Create page indicators
		this.pageIndicators = []
		const numPages = 3
		const xOffset = 25 * this.scaleFactor
		const startXpos = this.alertsBG.x - ((numPages - 1) * xOffset) / 2

		for (let i = 0; i < numPages; i++) {
			const indicator =
				i === 0 ? "activePageIndicator" : "inactivePageIndicator"
			const pageIndicator = this.add
				.image(
					startXpos + i * xOffset,
					this.alertsBG.y + 145 * this.scaleFactor,
					indicator,
				)
				.setDepth(10)
				.setScale(this.scaleFactor * 0.6)

			this.pageIndicators.push(pageIndicator)
			this.alertContentGroup.add(pageIndicator)
		}

		// Add all elements to the content group
		this.alertContentGroup.addMultiple([
			backButton,
			nextButton,
			nextText,
			blueAlertHolder,
			blueAlertText,
			clearAlertHolder,
			clearAlertText,
			toggleButton,
		])

		// Handle next button interaction
		nextButton.setInteractive().on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			if (!togglePressed) {
				return
			}

			if (this.tutorialHand) {
				this.tutorialHand.destroy()
			}

			if (isDone) {
				nextButton.setVisible(false)
				nextText.setVisible(false)
				// Return to alerts list
				this.alertContentGroup.clear(true, true)
				this.handleAlertCreation()
				let cockpitScene = this.scene.get("CockpitScene")
				destroyTooltip(this)
				//updateMiniLevel(this.savedMiniLevel)
				//handleGameProgressUpdate(cockpitScene)
				console.log("Done FACIAL SCAN")
				return
			}

			// Update page indicator
			this.pageIndicators[0].setTexture("inactivePageIndicator")
			this.pageIndicators[1].setTexture("activePageIndicator")

			// Hide UI elements for animation
			blueAlertHolder.setVisible(false)
			clearAlertHolder.setVisible(false)
			blueAlertText.setVisible(false)
			clearAlertText.setVisible(false)
			toggleButton.setVisible(false)
			backButton.setVisible(false)

			// Create and play animation
			let faceBG = this.add
				.image(
					this.alertsBG.x,
					this.alertsBG.y + 11 * this.scaleFactor,
					"faceBG",
				)
				.setScale(this.scaleFactor * 1)
				.setDepth(16)

			this.createAnimations()
			nextButton.setVisible(false)
			nextText.setVisible(false)
			this.scan.setDepth(16)
			this.scan.play("bio_scan")
			this.scan.on("animationcomplete", () => {
				isDone = true
				this.scan.setVisible(false)
				faceBG.setVisible(false)
				nextButton.setVisible(true)
				nextText.setVisible(true)
				nextButton.setVisible(false)
				nextText.setVisible(false)

				this.completionUI("facialScan")
				// Update button text
				nextText.setText("Done")
				nextText.setAlpha(1)
			})
		})
	}

	handleRestartDeviceAlert(backButton, nextButton, nextText, isDone) {
		nextButton.setVisible(false)
		nextText.setVisible(false)

		// Create alert holders
		const blueAlertHolder = this.add
			.image(
				this.alertsBG.x - 60 * this.scaleFactor,
				this.alertsBG.y + 50 * this.scaleFactor,
				"blueAlertContainer",
			)
			.setScale(this.scaleFactor)
			.setDepth(10)

		const blueAlertText = this.add
			.rexBBCodeText(
				blueAlertHolder.x,
				blueAlertHolder.y,
				"Press the \n[b]MENU BUTTON (BURGER MENU)[/b]\nto begin the process",
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 8 * this.scaleFactor,
					color: "white",
					align: "center",
					fontWeight: "400",
					resolution: 2,
				},
			)
			.setOrigin(0.5)
			.setDepth(10)
		this.alertContentGroup.add(blueAlertText)
		this.alertContentGroup.add(blueAlertHolder)

		const clearAlertHolder = this.add
			.image(
				this.alertsBG.x - 60 * this.scaleFactor,
				this.alertsBG.y - 50 * this.scaleFactor,
				"clearAlertWarning",
			)
			.setScale(this.scaleFactor * 0.5)
			.setDepth(10)

		const clearAlertText = this.add
			.text(
				clearAlertHolder.x + 18 * this.scaleFactor,
				clearAlertHolder.y,
				"The spaceships computer\nneeds to be shut down and\nstarted up again, remotely.",
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 8 * this.scaleFactor,
					color: "#11044E",
					align: "left",
					fontWeight: "400",
					resolution: 2,
				},
			)
			.setOrigin(0.5)
			.setDepth(10)
		this.alertContentGroup.add(clearAlertText)
		this.alertContentGroup.add(clearAlertHolder)
		// Create burger menu button
		const burgerButton = this.add
			.image(
				this.alertsBG.x + 150 * this.scaleFactor,
				this.alertsBG.y - 55 * this.scaleFactor,
				"burgerButton",
			)
			.setScale(this.scaleFactor * 0.5)
			.setDepth(10)
			.setInteractive()
		this.alertContentGroup.add(burgerButton)
		this.shutPills = this.add
			.image(
				burgerButton.x,
				burgerButton.y + 85 * this.scaleFactor,
				"alertShutPillsDefault",
			)
			.setScale(this.scaleFactor * 0.5)
			.setDepth(10)
			.setInteractive()
			.setVisible(false)

		this.alertContentGroup.add(this.shutPills)

		this.shutPillButton = this.add
			.image(
				burgerButton.x,
				burgerButton.y + 50 * this.scaleFactor,
				"activeButton",
			)
			.setScale(this.scaleFactor * 0.5, this.scaleFactor * 0.3)
			.setDepth(10)
			.setInteractive()
			.setAlpha(0.03)
			.setVisible(false)



		this.sleepPill = this.add
			.image(
				burgerButton.x,
				burgerButton.y + 90 * this.scaleFactor,
				"activeButton",
			)
			.setScale(this.scaleFactor * 0.5, this.scaleFactor * 0.3)
			.setDepth(10)
			.setInteractive()
			.setAlpha(0.03)
			.setVisible(false)

		this.sleepPill.on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			this.shutPills.setTexture("alertSleepPressed")
			this.wrongCaptchaElement()
			//this.shutPillButton.destroy()
			//this.sleepPill.destroy()
			//this.recentPill.destroy()
		})

		this.recentPill = this.add
			.image(
				burgerButton.x,
				burgerButton.y + 130 * this.scaleFactor,
				"activeButton",
			)
			.setScale(this.scaleFactor * 0.5, this.scaleFactor * 0.3)
			.setDepth(10)
			.setInteractive()
			.setAlpha(0.03)
			.setVisible(false)

		this.recentPill.on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			this.shutPills.setTexture("alertRecentPressed")
			this.wrongCaptchaElement()
			//this.shutPillButton.destroy()
			//this.sleepPill.destroy()
			//this.recentPill.destroy()
		})

		burgerButton.on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			destroyTooltip(this)
			blueAlertText.setText(
				"Select the correct option\nfrom the menu to [b]SHUT DOWN[/b]\nthe computer",
			)
			this.shutPills.setVisible(true)
			this.shutPillButton.setVisible(true)
			this.sleepPill.setVisible(true)
			this.recentPill.setVisible(true)
		})

		let indPosX = this.shutPillButton.x
		let indPosY = this.shutPillButton.y + 5 * this.scaleFactor

		this.shutPillButton.on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			this.shutPills.setTexture("alertShutPillsPressed")
			this.shutPillButton.destroy()
			this.sleepPill.destroy()
			this.recentPill.destroy()
			setTimeout(() => {
				burgerButton.setVisible(false)
				this.shutPills.setVisible(false)
				displayIndicator(
					this,
					indPosX,
					indPosY,
					1,
					"dischargeTip",
				)

				const numImages = 3
				const imageSpacing = 50 * this.scaleFactor
				const startImageX = clearAlertHolder.x + 160 * this.scaleFactor
				let imageNames = ["shutDownButton", "burgerButton", "playButton"]
				let shutDownButtons = []
				blueAlertText.setText(
					"In order to turn on (start up)\nthe computer again, press the\n[b] POWER BUTTON[/b]",
				)
				this.pageIndicators[0].setTexture("inactivePageIndicator")
				this.pageIndicators[1].setTexture("activePageIndicator")
				this.pageIndicators[2].setTexture("inactivePageIndicator")
				for (let i = 0; i < numImages; i++) {
					const imageX = startImageX + i * imageSpacing
					const imageY = clearAlertHolder.y + 55 * this.scaleFactor

					const image = this.add
						.image(imageX, imageY, imageNames[i])
						.setScale(this.scaleFactor * 0.5)
						.setDepth(10)

					shutDownButtons.push(image)

					if (i === 0) {
						image.setInteractive().on("pointerdown", () => {
							playUISound(this, this.soundManager, "Subtle click 1", 0.25)
							console.log("Shut Down Button Pressed?????")
							destroyIndicator(this)
							nextButton.setVisible(true)
							nextText.setVisible(true)
							nextText.setText("Done")

							blueAlertHolder.setVisible(false)
							blueAlertText.setVisible(false)
							clearAlertHolder.setVisible(false)
							clearAlertText.setVisible(false)

							shutDownButtons.forEach((button) => {
								button.setVisible(false)
							})

							this.pageIndicators[0].setTexture("inactivePageIndicator")
							this.pageIndicators[1].setTexture("inactivePageIndicator")
							this.pageIndicators[2].setTexture("activePageIndicator")

							nextButton.setInteractive().on("pointerdown", () => {
								playUISound(this, this.soundManager, "Subtle click 1", 0.25)
								console.log("Restart Button Pressed")
								// Return to alerts list
								blueAlertHolder.setVisible(false)
								blueAlertText.setVisible(false)
								clearAlertHolder.setVisible(false)
								clearAlertText.setVisible(false)
								this.alertContentGroup.clear(true, true)
								this.handleAlertCreation()
								let cockpitScene = this.scene.get("CockpitScene")
								nextButton.setVisible(false)
								nextText.setVisible(false)

								destroyTooltip(this)
								//updateMiniLevel(this.savedMiniLevel)
								//handleGameProgressUpdate(cockpitScene)
							})

							this.completionUI("restartDevice")
							//this.closeButton.setVisible(true)
							nextButton.setVisible(false)
							nextText.setVisible(false)
						})
					} else {
						image.setInteractive().on("pointerdown", () => {
							this.wrongCaptchaElement()
						})
					}
				}
			}, 1000)
		})

		// displayTooltip(
		// 	this,
		// 	burgerButton.x,

		// 	"small",
		// 	"Down",
		// 	"Press the burger menu",
		// 	14,
		// )
		const isMobile =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent,
			)

		let yOffset
		let fontScale
		let wordWrap = 1.01
		if (isMobile) {
			yOffset = 3
			fontScale = -7
		} else {
			yOffset = 6
			fontScale = -9
			wordWrap = 1.01
		}
		displayTooltip(this, {
			xPos: burgerButton.x,
			yPos: burgerButton.y + 55 * this.scaleFactor,
			type: "pressMeTip",
			scale: 1,
			text: "press me!",
			fontScale: fontScale,
			xTextOffset: 1,
			yTextOffset: yOffset,
			wrapWidth: wordWrap,
		})

		// Create page indicators
		this.pageIndicators = []
		const numPages = 3
		const xOffset = 25 * this.scaleFactor
		const startXpos = this.alertsBG.x - ((numPages - 1) * xOffset) / 2

		for (let i = 0; i < numPages; i++) {
			const indicator =
				i === 0 ? "activePageIndicator" : "inactivePageIndicator"
			const pageIndicator = this.add
				.image(
					startXpos + i * xOffset,
					this.alertsBG.y + 145 * this.scaleFactor,
					indicator,
				)
				.setDepth(10)
				.setScale(this.scaleFactor * 0.6)

			this.pageIndicators.push(pageIndicator)
			this.alertContentGroup.add(pageIndicator)
		}

		// Add all elements to the content group
		this.alertContentGroup.addMultiple([
			backButton,
			nextButton,
			nextText,
			blueAlertHolder,
			blueAlertText,
			clearAlertHolder,
			clearAlertText,
			burgerButton,
		])
	}

	handleSecurityCheckAlerts(backButton, nextButton, nextText, isDone) {
		// Create alert holders
		const blueAlertHolder = this.add
			.image(
				this.alertsBG.x - 105 * this.scaleFactor,
				this.alertsBG.y + 50 * this.scaleFactor,
				"blueAlertContainer",
			)
			.setScale(this.scaleFactor)
			.setDepth(10)

		const blueAlertText = this.add
			.rexBBCodeText(
				blueAlertHolder.x,
				blueAlertHolder.y,
				`[size=${10 * this.scaleFactor}]1/2[/size]\nSelect all the images on the\nright that contain\n\n[size=${25 * this.scaleFactor}][b]a mouse[/b][/size]`,
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 6 * this.scaleFactor,
					color: "white",
					align: "center",
					fontWeight: "700",
					resolution: 2,
				},
			)
			.setOrigin(0.5)
			.setDepth(10)

		const clearAlertHolder = this.add
			.image(
				this.alertsBG.x - 105 * this.scaleFactor,
				this.alertsBG.y - 50 * this.scaleFactor,
				"clearAlertWarning",
			)
			.setScale(this.scaleFactor * 0.5)
			.setDepth(10)

		const clearAlertText = this.add
			.text(
				clearAlertHolder.x + 10 * this.scaleFactor,
				clearAlertHolder.y,
				"Complete the test to\nimprove your skills",
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 8 * this.scaleFactor,
					color: "#11044E",
					align: "center",
					fontWeight: "700",
					resolution: 2,
				},
			)
			.setOrigin(0.5)
			.setDepth(10)

		// Create page indicators
		this.pageIndicators = []
		const numPages = 3
		const xOffset = 25 * this.scaleFactor
		const startXpos = this.alertsBG.x - ((numPages - 1) * xOffset) / 2

		for (let i = 0; i < numPages; i++) {
			const indicator =
				i === 0 ? "activePageIndicator" : "inactivePageIndicator"
			const pageIndicator = this.add
				.image(
					startXpos + i * xOffset,
					this.alertsBG.y + 145 * this.scaleFactor,
					indicator,
				)
				.setDepth(10)
				.setScale(this.scaleFactor * 0.6)

			this.pageIndicators.push(pageIndicator)
			this.alertContentGroup.add(pageIndicator)
		}

		nextButton.setAlpha(1)
		nextText.setAlpha(1)

		this.captchasDone = 0
		this.correctImages = [2, 8, 9] // Correct images to be selected
		this.createCaptchaElements(clearAlertHolder, nextButton, nextText, "mouse")

		// Add all elements to the content group
		this.alertContentGroup.addMultiple([
			backButton,
			nextButton,
			nextText,
			blueAlertHolder,
			blueAlertText,
			clearAlertHolder,
			clearAlertText,
		])

		// Handle next button interaction
		nextButton.setInteractive().on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)

			// Check if any images are selected
			if (this.selectedImages.length === 0) {
				this.wrongCaptchaElement()
				return
			}

			// Check if selected images match correct images (order doesn't matter)
			const selectedSet = new Set(this.selectedImages)
			const correctSet = new Set(this.correctImages)

			const isCorrect =
				selectedSet.size === correctSet.size &&
				[...selectedSet].every((img) => correctSet.has(img))

			if (!isCorrect) {
				this.wrongCaptchaElement()
				this.selectorIcons.forEach((icon) => icon.destroy())
				this.selectedImages = []
				this.selectorIcons = []
				return
			} else {
				this.selectedImages = []
				this.captchasDone++
				this.selectorIcons.forEach((icon) => icon.destroy())
				this.selectorIcons = []
				nextButton.setAlpha(1)
				nextText.setAlpha(1)

				if (this.captchasDone === 1) {
					blueAlertText.setText(
						//"2/2\nSelect all the images on the\nright that contain a printer",
						`[size=${10 * this.scaleFactor}]2/2[/size]\nSelect all the images on the\nright that contain\n\n[size=${25 * this.scaleFactor}][b]a printer[/b][/size]`,
					)
					this.correctImages = [4, 5, 6] // Correct images for second captcha
				}
			}

			if (this.captchasDone === 2) {
				this.alertContentGroup.clear(true, true)
				nextButton.setVisible(false)
				nextText.setVisible(false)

				this.completionUI("securityCheck")
				return
			}

			// Update page indicator
			this.pageIndicators[0].setTexture("inactivePageIndicator")
			this.pageIndicators[1].setTexture("activePageIndicator")

			// Create new elements for second captcha
			this.createCaptchaElements(
				clearAlertHolder,
				nextButton,
				nextText,
				"printer",
			)
		})
	}

	handleReconnectAlert(backButton, nextButton, nextText, isDone) {
		let correctWifiBool = false
		let checkWifiBool = false

		nextText.setText("CONNECT")
		nextButton.setTexture("normalWideBtn")
		nextButton.setScale(this.scaleFactor * 0.5)
		nextButton.x -= 25 * this.scaleFactor
		nextText.x -= 25 * this.scaleFactor
		const blueAlertString =
			"Reconnect by turning on\nthe [b]Wi-Fi[/b] using the status bar\non the right"
		const clearAlertString = "Connection manager"
		let togglePressed = false
		let xPositionOffset = 120

		// Create alert holders
		const blueAlertHolder = this.add
			.image(
				this.alertsBG.x - xPositionOffset * this.scaleFactor,
				this.alertsBG.y + 50 * this.scaleFactor,
				"blueAlertContainer",
			)
			.setScale(this.scaleFactor)
			.setDepth(10)

		const blueAlertText = this.add
			.rexBBCodeText(blueAlertHolder.x, blueAlertHolder.y, blueAlertString, {
				fontFamily: "Space Grotesk",
				fontSize: this.fontSize - 5 * this.scaleFactor,
				color: "white",
				align: "center",
				fontWeight: "400",
				resolution: 2,
			})
			.setOrigin(0.5)
			.setDepth(10)

		const clearAlertHolder = this.add
			.image(
				this.alertsBG.x - xPositionOffset * this.scaleFactor,
				this.alertsBG.y - 60 * this.scaleFactor,
				"clearAlertWarning",
			)
			.setScale(this.scaleFactor * 0.5)
		//.setDepth(10)

		const clearAlertText = this.add
			.text(clearAlertHolder.x + 10 * this.scaleFactor, clearAlertHolder.y, clearAlertString, {
				fontFamily: "Space Grotesk",
				fontSize: this.fontSize - 6 * this.scaleFactor,
				color: "#11044E",
				align: "center",
				fontWeight: "400",
				resolution: 2,
			})
			.setOrigin(0.5)
		//.setDepth(10)

		// Create toggle button
		const wifiDisconnected = this.add
			.image(
				this.alertsBG.x + 20 * this.scaleFactor,
				this.alertsBG.y - 90 * this.scaleFactor,
				"wifiDisconnected",
			)
			.setScale(this.scaleFactor * 0.55)
			.setDepth(10)
			.setInteractive()
			.setOrigin(0)

		const checkWifiNetwork = () => {
			checkWifiBool = true
			if (correctWifiBool) {

				this.displayWifiTipBool = false

				playUISound(this, this.soundManager, "Subtle click 1", 0.25)
				correctWifiBool = true
				blueAlertText.setText(
					"Enter the Wi-Fi password\nin the field [b]exactly[/b] as shown,\nincluding capital letters and\nspecial characters.\n\nThen press NEXT to connect",
				)
				blueAlertText.setFontSize(this.fontSize - 7 * this.scaleFactor)
				correctWifi.setVisible(false)
				wrongWifi01.setVisible(false)
				wrongWifi02.setVisible(false)


				nextText.setText("NEXT")
				nextButton.setTexture("saveBtn")
				nextButton.setScale(this.scaleFactor)
				nextButton.x += 25 * this.scaleFactor
				nextText.x += 25 * this.scaleFactor
				nextButton.setAlpha(0.5)
				nextButton.input.enabled = false
				nextText.setAlpha(0.5)
				// Update page indicator
				this.pageIndicators[0].setTexture("inactivePageIndicator")
				this.pageIndicators[1].setTexture("activePageIndicator")

				// Create number input element
				numberInput = document.createElement("input")
				numberInput.id = "activity-number-input"
				numberInput.type = "text"
				numberInput.min = "0" // Minimum value allowed
				numberInput.max = "100" // Maximum value allowed

				// Apply styles for number input
				numberInput.style.position = "absolute"
				numberInput.style.left = `${clearAlertHolder.x + 170 * this.scaleFactor
					}px`
				numberInput.style.top = `${clearAlertHolder.y - 20 * this.scaleFactor
					}px`
				numberInput.style.width = `${180 * this.scaleFactor}px`
				numberInput.style.height = `${40 * this.scaleFactor}px`
				numberInput.style.fontSize = `${18 * this.scaleFactor}px`
				numberInput.style.fontFamily = "Space Grotesk, sans-serif"
				numberInput.style.padding = "5px"
				numberInput.style.borderRadius = "8px"
				numberInput.style.border = "1px solid #3E27BE"
				numberInput.style.background = "#ffffff"
				numberInput.style.color = "#11044E"
				numberInput.style.zIndex = "1000"
				numberInput.style.textAlign = "left"

				this.wifiPasswordReference = numberInput

				numberInput.addEventListener("pointerdown", function (event) {
					console.log("Number input touched")

					if (!this.displayWifiTipBool) {
						displayTooltip(this, {
							xPos: wifiDisconnected.x - 60 * this.scaleFactor,
							yPos: wifiDisconnected.y + 30 * this.scaleFactor,
							type: "wiFiTooltip",
							scale: 1,
							text: "[b]Wifi Password:[/b] $H!P_5h@pe",
							fontScale: -9,
							xTextOffset: -4,
							yTextOffset: 0.1 * this.scaleFactor,
						})
					}

					this.displayWifiTipBool = true
				}.bind(this))


				numberInput.addEventListener("input", function (event) {

					console.log("Number input value:", numberInput.value)
					nextButton.setAlpha(1)
					nextText.setAlpha(1)
					nextButton.input.enabled = true
				})

				document.addEventListener("touchstart", function (event) {
					// If the clicked element isn't the input and isn't a child of the input
					if (
						event.target !== numberInput &&
						!numberInput.contains(event.target)
					) {
						numberInput.blur()
					}
				})

				// // Handle touch events to blur input field
				// document.addEventListener("touchstart", (event) => {
				// 	if (
				// 		numberInput.contains(event.target) &&						
				// 	) {
				// 		this.inputField.blur()
				// 	}
				// })

				wifiPasswordText = this.add
					.text(
						clearAlertHolder.x + 250 * this.scaleFactor,
						clearAlertHolder.y + 35 * this.scaleFactor,
						"Wifi password: $H!P_5h@pe",
						{
							fontFamily: "Space Grotesk",
							fontSize: this.fontSize - 5 * this.scaleFactor,
							color: "#11044E",
							align: "left",
							resolution: 2,
						},
					)
					.setOrigin(0.5)
					.setDepth(10)
					//.setVisible(false)

				wifiPasswordText.resolution = 2

				wifiDisconnected.setVisible(false)
				// Add the number input element directly to the document body
				//document.body.appendChild(numberInput)
				// Add to the HTML UI container instead of body
				const htmlContainer = document.getElementById("html-ui-container")
				htmlContainer.appendChild(numberInput)

				// IMPORTANT: Add this function to sync the HTML container with canvas scaling
				const syncHTMLContainer = () => {
					const canvas = this.game.canvas
					const htmlContainer = document.getElementById("html-ui-container")
					const gameContainer = document.getElementById("game")

					if (!htmlContainer || !canvas || !gameContainer) return

					// Get canvas and container dimensions
					const canvasRect = canvas.getBoundingClientRect()
					const containerRect = gameContainer.getBoundingClientRect()

					// Calculate the scale factor that Phaser is using
					const scaleX = canvasRect.width / this.game.config.width
					const scaleY = canvasRect.height / this.game.config.height
					const scale = Math.min(scaleX, scaleY)

					// Calculate offset for centering (letterboxing)
					const offsetX = (containerRect.width - (this.game.config.width * scale)) / 2
					const offsetY = (containerRect.height - (this.game.config.height * scale)) / 2

					// Apply the same transform to HTML container
					htmlContainer.style.transform = `scale(${scale}) translate(${offsetX / scale}px, ${offsetY / scale}px)`
					htmlContainer.style.transformOrigin = 'top left'
				}

				// Initial sync
				syncHTMLContainer()
			} else {
				this.wrongCaptchaElement()
			}
		}

		let correctWifi = this.add
			.image(
				this.alertsBG.x + 115 * this.scaleFactor,
				this.alertsBG.y - 5 * this.scaleFactor,
				"activeButton",
			)
			.setScale(this.scaleFactor * 0.75, this.scaleFactor * 0.2)
			.setDepth(10)
			.setAlpha(0.01)
			.setInteractive()
			.setVisible(false)

		correctWifi.on("pointerdown", () => {
			//correctWifi.setAlpha(0.5)
			wifiDisconnected.setTexture("wifiDisconnected4")
			correctWifiBool = true
			nextButton.setAlpha(1)
			nextText.setAlpha(1)
			nextButton.setInteractive()
			// wrongWifi01.input.enabled = false
			// wrongWifi02.input.enabled = false
		})

		let wrongWifi01 = this.add
			.image(
				this.alertsBG.x + 115 * this.scaleFactor,
				this.alertsBG.y + 35 * this.scaleFactor,
				"activeButton",
			)
			.setScale(this.scaleFactor * 0.75, this.scaleFactor * 0.2)
			.setDepth(10)
			.setAlpha(0.01)
			.setInteractive()
			.setVisible(false)

		wrongWifi01.on("pointerdown", () => {
			//wrongWifi01.setAlpha(0.5);
			wifiDisconnected.setTexture("wrongWifi00")
			this.wrongCaptchaElement()
			nextButton.setAlpha(0.5)
			nextText.setAlpha(0.5)
			//nextButton.setInteractive()
			// correctWifi.input.enabled = false
			// wrongWifi02.input.enabled = false
		})

		let wrongWifi02 = this.add
			.image(
				this.alertsBG.x + 115 * this.scaleFactor,
				this.alertsBG.y + 70 * this.scaleFactor,
				"activeButton",
			)
			.setScale(this.scaleFactor * 0.75, this.scaleFactor * 0.2)
			.setDepth(10)
			.setAlpha(0.01)
			.setInteractive()
			.setVisible(false)

		wrongWifi02.on("pointerdown", () => {
			//wrongWifi02.setAlpha(0.5);
			wifiDisconnected.setTexture("wrongWifi01")
			this.wrongCaptchaElement()
			nextButton.setAlpha(0.5)
			nextText.setAlpha(0.5)
			//nextButton.setInteractive()
			// correctWifi.input.enabled = false
			// wrongWifi01.input.enabled = false
		})

		let state = 0
		let numberInput
		let wifiPasswordText
		wifiDisconnected.on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			if (this.tutorialHand) {
				this.tutorialHand.destroy()
			}
			if (state === 0) {
				state = 1
				wifiDisconnected.setTexture("wifiDisconnected2")
				destroyTooltip(this)
				destroyIndicator(this)
				blueAlertText.setText(
					"Turn on the Wi-Fi using the\n[b]toggle button[/b]",
				)
				return
			}
			if (state === 1) {
				//state = 2
				wifiDisconnected.setTexture("wifiDisconnected3")
				wifiDisconnected.input.enabled = false
				blueAlertText.setText(
					"[b]Press on the network[/b]\nyou need to connect to\nfrom the list on the right",
				)
				correctWifi.setVisible(true)
				wrongWifi01.setVisible(true)
				wrongWifi02.setVisible(true)
				return
			}
			if (state === 2) {
				state = 3
				wifiDisconnected.setTexture("wifiDisconnected4")
				nextButton.on("pointerdown", () => {
					checkWifiNetwork()
				})
				return
			}
			if (state === 3) {
				state = 4
				return
			}

			//nextButton.setAlpha(1);
			//nextText.setAlpha(1);
		})

		const isMobile =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent,
			)
		let yOffset
		let fontScale
		let wordWrap = 1.01
		if (isMobile) {
			yOffset = 3
			fontScale = -7
		} else {
			yOffset = 6
			fontScale = -7
			wordWrap = 1.01
		}
		let xPosOffset = 105
		displayTooltip(this, {
			xPos: wifiDisconnected.x + xPosOffset * this.scaleFactor,
			yPos: wifiDisconnected.y + 60 * this.scaleFactor,
			type: "stepBackTip",
			scale: 1,
			text: "press the wifi\nicon to enable!",
			fontScale: fontScale,
			xTextOffset: 1,
			yTextOffset: yOffset,
			wrapWidth: wordWrap,
		})

		// displayTooltip(this, {
		// 	xPos: wifiDisconnected.x - 110 * this.scaleFactor,
		// 	yPos: wifiDisconnected.y + 10 * this.scaleFactor,
		// 	type: "incorrectWifiTooltip",
		// 	scale: 1.05,
		// 	text: "Password incorrect. Please try again.\n[b]Wifi Password:[/b] $H!P_5h@pe",
		// 	fontScale: -9,
		// 	xTextOffset: -4,
		// 	yTextOffset: 0.1 * this.scaleFactor,
		// 	textColor: "#fff"
		// 	//wrapWidth: wordWrap,
		// })

		displayTutorialHand(this, {
			xPos: wifiDisconnected.x + 178 * this.scaleFactor,
			yPos: wifiDisconnected.y - 40 * this.scaleFactor,
			rotation: -110,
		})

		this.tutorialHand.setDepth(50000)
		displayIndicator(
			this,
			wifiDisconnected.x + xPosOffset * this.scaleFactor,
			wifiDisconnected.y + 10 * this.scaleFactor,
			1.2,
			"wifiIndicator",
		)

		// Create page indicators
		this.pageIndicators = []
		const numPages = 3
		const xOffset = 25 * this.scaleFactor
		const startXpos = this.alertsBG.x - ((numPages - 1) * xOffset) / 2

		for (let i = 0; i < numPages; i++) {
			const indicator =
				i === 0 ? "activePageIndicator" : "inactivePageIndicator"
			const pageIndicator = this.add
				.image(
					startXpos + i * xOffset,
					this.alertsBG.y + 145 * this.scaleFactor,
					indicator,
				)
				.setDepth(10)
				.setScale(this.scaleFactor * 0.6)

			this.pageIndicators.push(pageIndicator)
			this.alertContentGroup.add(pageIndicator)
		}

		// Add all elements to the content group
		this.alertContentGroup.addMultiple([
			backButton,
			nextButton,
			nextText,
			blueAlertHolder,
			blueAlertText,
			clearAlertHolder,
			clearAlertText,
			wifiDisconnected,
		])

		// Handle next button interaction
		nextButton.setInteractive().on("pointerdown", () => {
			if (checkWifiBool) {
				this.displayWifiError = false
				playUISound(this, this.soundManager, "Subtle click 1", 0.25)
				if (isDone) {
					//     // Return to alerts list
					//  this.alertContentGroup.clear(true, true);

					//  this.createNoNewAlerts();
					//  //let cockpitScene = this.scene.get('CockpitScene');
					//  destroyTooltip(this);
					//  //updateMiniLevel(this.savedMiniLevel);
					//     //handleGameProgressUpdate(cockpitScene);
					//     console.log("Done WIFI");
					return
				}

				if (numberInput.value !== "$H!P_5h@pe") {
					console.log("Wrong password!")
					// wifiPasswordText.setText(
					// 	"Password incorrect. Please try again.\nWifi password : $H!P_5h@pe",
					// )
					if (!this.displayWifiError) {
						displayTooltip(this, {
							xPos: wifiDisconnected.x - 90 * this.scaleFactor,
							yPos: wifiDisconnected.y + 30 * this.scaleFactor,
							type: "incorrectWifiTooltip",
							scale: 1.05,
							text: "Password incorrect. Please try again.\n[b]Wifi Password:[/b] $H!P_5h@pe",
							fontScale: -9,
							xTextOffset: -4,
							yTextOffset: 0.1 * this.scaleFactor,
							textColor: "#fff"
							//wrapWidth: wordWrap,
						})
					}

					this.displayWifiError = true

					playErrorAnimation(this, {
						includeTablet: false
					})
					nextButton.setAlpha(0.5)
					nextButton.input.enabled = false
					nextText.setAlpha(0.5)

					// wifiPasswordText.setPosition(
					// 	clearAlertHolder.x + 250 * this.scaleFactor,
					// 	clearAlertHolder.y - 39 * this.scaleFactor,
					// )
					// wifiPasswordText.setFontSize(
					// 	this.fontSize - 6 * this.scaleFactor,
					// )
					// wifiPasswordText.setColor("#B50042")
					numberInput.style.borderColor = "#B50042"

					//numberInput.value = "wrong password" // Clear the input field
				} else {
					destroyTooltip(this)
					numberInput.remove() // Remove the input field from the document
					wifiPasswordText.destroy() // Remove the password text
					blueAlertHolder.setVisible(false)
					clearAlertHolder.setVisible(false)
					blueAlertText.setVisible(false)
					clearAlertText.setVisible(false)
					wifiDisconnected.setVisible(false)
					nextText.setText("Done")
					nextText.setAlpha(1)
					nextButton.setVisible(false)
					nextText.setVisible(false)
					this.completionUI("reconnectDevice")
				}
			} else {
				checkWifiNetwork()
			}
		})
	}

	handleRobotAlert(backButton, nextButton, nextText, isDone) {
		let alertHeader = this.add
			.image(
				this.alertsBG.x,
				this.alertsBG.y - 70 * this.scaleFactor,
				"alertRobotHeader",
			)
			.setScale(this.scaleFactor * 0.5)
			.setDepth(10)

		// Track original positions for returning pills if placed incorrectly
		const originalPositions = {}

		// Create page indicators
		this.pageIndicators = []
		const numPages = 2
		const xOffset = 25 * this.scaleFactor
		const startXpos = this.alertsBG.x - ((numPages - 1) * xOffset) / 2

		for (let i = 0; i < numPages; i++) {
			const indicator =
				i === 0 ? "activePageIndicator" : "inactivePageIndicator"
			const pageIndicator = this.add
				.image(
					startXpos + i * xOffset,
					this.alertsBG.y + 145 * this.scaleFactor,
					indicator,
				)
				.setDepth(10)
				.setScale(this.scaleFactor * 0.6)

			this.pageIndicators.push(pageIndicator)
			this.alertContentGroup.add(pageIndicator)
		}

		// Create the desktop pill with drag functionality
		let desktopPill = this.add
			.image(
				this.alertsBG.x - 160 * this.scaleFactor,
				this.alertsBG.y + 40 * this.scaleFactor,
				"alertDesktop",
			)
			.setScale(this.scaleFactor * 0.25)
			.setDepth(16)
			.setInteractive()

		// Store original position
		originalPositions.desktop = {
			x: desktopPill.x,
			y: desktopPill.y,
		}

		// Create the laptop pill with drag functionality
		let laptopPill = this.add
			.image(
				desktopPill.x + 90 * this.scaleFactor,
				desktopPill.y,
				"alertLaptop",
			)
			.setScale(this.scaleFactor * 0.25)
			.setDepth(16)
			.setInteractive()

		// Store original position
		originalPositions.laptop = {
			x: laptopPill.x,
			y: laptopPill.y,
		}

		// Create holders
		let laptopHolder = this.add
			.image(
				this.alertsBG.x + 70 * this.scaleFactor,
				this.alertsBG.y + 40 * this.scaleFactor,
				"alertLaptopContainer",
			)
			.setScale(this.scaleFactor * 0.25)
			.setDepth(10)

		let desktopHolder = this.add
			.image(
				laptopHolder.x + 100 * this.scaleFactor,
				laptopHolder.y,
				"alertPcContainer",
			)
			.setScale(this.scaleFactor * 0.25)
			.setDepth(10)

		let laptopHolderHeader = this.add
			.image(
				laptopHolder.x,
				laptopHolder.y - 55 * this.scaleFactor,
				"alertLaptopHeader",
			)
			.setScale(this.scaleFactor * 0.25)
			.setDepth(10)

		let desktopHolderHeader = this.add
			.image(
				desktopHolder.x,
				desktopHolder.y - 55 * this.scaleFactor,
				"alertDesktopHeader",
			)
			.setScale(this.scaleFactor * 0.25)
			.setDepth(10)

		// Track placement status
		const placement = {
			desktopCorrect: false,
			laptopCorrect: false,
		}

		// Enable drag for desktop pill
		this.input.setDraggable(desktopPill)
		desktopPill.on("dragstart", function (pointer) {
			//playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			//this.setTint(0x44ff44);
			desktopPill.setDepth(17)
		})

		desktopPill.on("drag", function (pointer, dragX, dragY) {
			this.x = dragX
			this.y = dragY
		})

		const scene = this

		desktopPill.on("dragend", function (pointer) {
			this.clearTint()

			// Check if the pill is dropped in the correct holder
			if (
				Phaser.Geom.Rectangle.Overlaps(
					this.getBounds(),
					desktopHolder.getBounds(),
				)
			) {
				// Snap to center of holder
				this.x = desktopHolder.x
				this.y = desktopHolder.y
				placement.desktopCorrect = true
			} else {
				// Return to original position
				this.x = originalPositions.desktop.x
				this.y = originalPositions.desktop.y
				placement.desktopCorrect = false
				scene.wrongCaptchaElement() // Play error animation if placed incorrectly				
			}

			// Check if both pills are correctly placed
			checkCompletion()
		})

		// Enable drag for laptop pill
		this.input.setDraggable(laptopPill)
		laptopPill.on("dragstart", function (pointer) {
			//playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			//this.setTint(0x44ff44);
			laptopPill.setDepth(17)
		})

		laptopPill.on("drag", function (pointer, dragX, dragY) {
			this.x = dragX
			this.y = dragY
		})

		laptopPill.on("dragend", function (pointer) {
			this.clearTint()

			// Check if the pill is dropped in the correct holder
			if (
				Phaser.Geom.Rectangle.Overlaps(
					this.getBounds(),
					laptopHolder.getBounds(),
				)
			) {
				// Snap to center of holder
				this.x = laptopHolder.x
				this.y = laptopHolder.y
				placement.laptopCorrect = true
			} else {
				// Return to original position
				this.x = originalPositions.laptop.x
				this.y = originalPositions.laptop.y
				placement.laptopCorrect = false
				scene.wrongCaptchaElement()
			}

			// Check if both pills are correctly placed
			checkCompletion()
		})

		// Function to check if both pills are correctly placed
		const checkCompletion = () => {
			if (placement.desktopCorrect && placement.laptopCorrect) {
				console.log("its done")
				nextButton.setAlpha(1)
				nextText.setAlpha(1)
				nextButton.setInteractive().on("pointerdown", () => {
					playUISound(this, this.soundManager, "Subtle click 1", 0.25)
					console.log("We show Done and allat other stuff")
					// Update page indicator
					this.pageIndicators[0].setTexture("inactivePageIndicator")
					this.pageIndicators[1].setTexture("activePageIndicator")
					laptopHolder.setVisible(false)
					laptopHolderHeader.setVisible(false)
					laptopPill.setVisible(false)
					desktopHolder.setVisible(false)
					desktopHolderHeader.setVisible(false)
					desktopPill.setVisible(false)
					alertHeader.setVisible(false)
					nextButton.setVisible(false)
					nextText.setVisible(false)

					this.completionUI("robotAlert")
				})
				// Here you can add any additional actions when complete
				// if (isDone && typeof isDone === 'function') {
				//     //isDone();
				// }
			}
		}
	}

	// HTML OTP Input implementation for Phaser
	handleOtpAlert(backButton, nextButton, nextText, isDone) {
		nextText.setText("VERIFY")
		let isCompleted = false
		let alertHeader = this.add
			.image(
				this.alertsBG.x,
				this.alertsBG.y - 50 * this.scaleFactor,
				"alertOTPHeader",
			)
			.setScale(this.scaleFactor * 0.5)
			.setDepth(10)

		// Create page indicators
		this.pageIndicators = []
		const numPages = 2
		const xOffset = 25 * this.scaleFactor
		const startXpos = this.alertsBG.x - ((numPages - 1) * xOffset) / 2

		for (let i = 0; i < numPages; i++) {
			const indicator =
				i === 0 ? "activePageIndicator" : "inactivePageIndicator"
			const pageIndicator = this.add
				.image(
					startXpos + i * xOffset,
					this.alertsBG.y + 145 * this.scaleFactor,
					indicator,
				)
				.setDepth(10)
				.setScale(this.scaleFactor * 0.6)

			this.pageIndicators.push(pageIndicator)
			this.alertContentGroup.add(pageIndicator)
		}

		let wrongOTPHeaderText = "alertOTPHeaderWrong"
		let correctOTPHeaderText = "alertOTPHeaderCorrect"

		// Constants for styling
		const borderColorNormal = "#CCCCCC" // Grey (default state)
		const borderColorFocus = "#5C58EB" // Purple (focused state)
		const borderColorError = "#C41E3A" // Crimson
		const borderColorSuccess = "#00B0CB" // Blue
		const correctOtp = "7630" // The correct OTP code

		// Get the position for placing HTML elements below the alertHeader
		const gameCanvas = this.game.canvas
		const canvasRect = gameCanvas.getBoundingClientRect()

		// Calculate position based on alertsBG - assuming alertsBG is centered
		const headerBottom = this.alertsBG.y // position below header

		// Create HTML container for OTP input
		this.otpContainer = document.createElement("div")
		this.otpContainer.id = "otp-container"
		this.otpContainer.style.position = "absolute"
		this.otpContainer.style.left = "50%"
		this.otpContainer.style.transform = "translateX(-50%)"
		this.otpContainer.style.top = `${headerBottom + 12 * this.scaleFactor}px`
		this.otpContainer.style.display = "flex"
		this.otpContainer.style.flexDirection = "column"
		this.otpContainer.style.alignItems = "center"
		this.otpContainer.style.zIndex = "1000"
		this.otpContainer.style.width = "100%"
		this.otpContainer.style.maxWidth = "300px"

		// Rest of the code remains the same...
		// Create inputs container
		const inputsContainer = document.createElement("div")
		inputsContainer.style.display = "flex"
		inputsContainer.style.gap = "10px"
		inputsContainer.style.justifyContent = "center"
		inputsContainer.style.width = "100%"
		this.otpContainer.appendChild(inputsContainer)

		let verifyImage = this.add
			.image(
				alertHeader.x + 135 * this.scaleFactor,
				alertHeader.y + 90 * this.scaleFactor,
				"otpCorrect",
			)
			.setScale(this.scaleFactor * 0.5)
			.setVisible(false)
		verifyImage.setDepth(10)
		this.alertContentGroup.add(verifyImage)

		// Create 4 input fields
		const inputs = []
		for (let i = 0; i < 4; i++) {
			const inputWrapper = document.createElement("div")
			inputWrapper.style.position = "relative"

			const input = document.createElement("input")
			input.type = "number"
			input.inputMode = "numeric"
			input.pattern = "[0-9]"
			input.maxLength = 1
			input.style.width = `${50 * this.scaleFactor}px`
			input.style.height = `${60 * this.scaleFactor}px`
			input.style.fontSize = "28px"
			input.style.fontFamily = '"Space Grotesk", sans-serif'
			input.style.textAlign = "center"
			input.style.border = `3px solid ${borderColorNormal}` // Start with grey
			input.style.borderRadius = "10px"
			input.style.outline = "none"
			input.style.backgroundColor = "white"

			// Focus styling - change to blue when focused
			input.addEventListener("focus", () => {
				input.style.borderColor = borderColorFocus
				input.style.boxShadow = `0 0 0 2px ${borderColorFocus}40`
			})

			// Blur styling - return to grey when not focused (unless filled, in error, or success state)
			input.addEventListener("blur", () => {
				input.style.boxShadow = "none"
				// Only return to grey if not filled and not in error or success state
				if (
					input.value === "" &&
					input.style.borderColor !== borderColorError &&
					input.style.borderColor !== borderColorSuccess
				) {
					input.style.borderColor = borderColorNormal
				}
			})

			// Setup input behavior
			input.addEventListener("input", (e) => {
				// Allow only numbers
				e.target.value = e.target.value.replace(/[^0-9]/g, "")

				// Reset error state if any
				resetErrorState()

				// Keep border blue when filled
				if (e.target.value) {
					e.target.style.borderColor = borderColorFocus
				}

				// Auto advance to next input
				if (e.target.value && i < 3) {
					inputs[i + 1].focus()
				}

				// Check if all inputs are filled
				checkLength()
			})

			// Handle backspace
			input.addEventListener("keydown", (e) => {
				if (e.key === "Backspace" && !e.target.value && i > 0) {
					inputs[i - 1].focus()
					// Prevent the default behavior which would delete the previous input's value
					e.preventDefault()
				} else if (e.key === "Backspace" && e.target.value) {
					// When backspacing to clear the field, it will become empty
					// The input event will handle the color change
				}
			})

			inputWrapper.appendChild(input)
			inputsContainer.appendChild(inputWrapper)
			inputs.push(input)
		}

		// Add container for the Next button (below the inputs)
		const buttonContainer = document.createElement("div")
		buttonContainer.style.marginTop = "20px"
		buttonContainer.style.width = "100%"
		buttonContainer.style.display = "flex"
		buttonContainer.style.justifyContent = "center"
		this.otpContainer.appendChild(buttonContainer)

		// Append the OTP container to the game container
		this.gameContainer = this.game.canvas.parentElement
		this.gameContainer.appendChild(this.otpContainer)

		// Focus the first input
		setTimeout(() => inputs[0].focus(), 100)

		const checkLength = () => {
			const enteredOtp = inputs.map((input) => input.value).join("")

			if (enteredOtp.length === 4) {
				nextButton.setAlpha(1)
				nextText.setAlpha(1)
				nextButton.setInteractive()
			} else {
				if (nextButton) {
					nextButton.setAlpha(0.5)
					nextButton.disableInteractive()
				}
			}
		}
		// Function to check if OTP is complete
		const checkCompletion = () => {
			const enteredOtp = inputs.map((input) => input.value).join("")

			if (enteredOtp.length === 4) {
				if (enteredOtp === correctOtp) {
					setSuccessState()
					isCompleted = true
					nextText.setText("FINISH")
					alertHeader.setTexture(correctOTPHeaderText)
					verifyImage.setVisible(true)
					verifyImage.setTexture("otpCorrect")
					nextText.setAlpha(1)
					if (nextButton) {
						nextButton.setAlpha(1)
						nextButton.setInteractive()
					}
				} else {
					setErrorState()
					// Update Phaser header texture
					alertHeader.setTexture(wrongOTPHeaderText)
					verifyImage.setVisible(true)
					verifyImage.setTexture("otpError")
					playErrorAnimation(this, {
						includeTablet: false
					})
					// Clear inputs for retry
					setTimeout(() => {
						inputs.forEach((input) => {
							input.value = ""
							input.style.borderColor = borderColorNormal // Set all to grey
						})
						inputs[0].focus() // This will trigger the focus event and make the first input blue
					}, 800)
				}
			} else {
				if (nextButton) {
					nextButton.setAlpha(0.5)
					nextButton.disableInteractive()
				}
			}
		}

		// Function to set error state
		const setErrorState = () => {
			inputs.forEach((input) => {
				input.style.borderColor = borderColorError
			})

			// Update Phaser header texture
			alertHeader.setTexture(wrongOTPHeaderText)

			nextButton.setAlpha(0.5)
			nextButton.disableInteractive()
			nextText.setAlpha(0.5)
		}

		// Function to set success state
		const setSuccessState = () => {
			inputs.forEach((input) => {
				input.style.borderColor = borderColorSuccess
			})
		}

		// Function to reset error state
		const resetErrorState = () => {
			inputs.forEach((input) => {
				// If input has value, keep it blue; if empty, return to grey
				if (input.value) {
					input.style.borderColor = borderColorFocus
				} else {
					input.style.borderColor = borderColorNormal
				}
			})

			// Reset header texture
			alertHeader.setTexture("alertOTPHeader")
			verifyImage.setVisible(false)
		}

		// Add click handler to Phaser next button if it exists
		if (nextButton) {
			nextButton.on("pointerdown", () => {
				playUISound(this, this.soundManager, "Subtle click 1", 0.25)
				if (!isCompleted) {
					checkCompletion()
				} else {
					// Only proceed if OTP is valid
					const enteredOtp = inputs.map((input) => input.value).join("")
					if (enteredOtp === correctOtp) {
						// Clean up HTML elements
						cleanUp()

						// Call the done callback
						if (isDone && typeof isDone === "function") {
							isDone(enteredOtp)
						}
					}
				}
			})
		}
		// Add cleanup function for both normal and error cases
		const cleanUp = () => {
			if (this.gameContainer.contains(this.otpContainer)) {
				this.gameContainer.removeChild(this.otpContainer)

				this.pageIndicators[0].setTexture("inactivePageIndicator")
				this.pageIndicators[1].setTexture("activePageIndicator")
				nextButton.setVisible(false)
				nextText.setVisible(false)
				verifyImage.setVisible(false)
				this.completionUI("otpAlert")
			}

			// Remove event listeners from Phaser buttons
			this.input.keyboard.off("keydown")

			// Destroy the Phaser header image
			if (alertHeader) {
				alertHeader.destroy()
			}
		}

		// Store references for potential external access
		this.otpElements = {
			container: this.otpContainer,
			inputs: inputs,
			alertHeader: alertHeader,
		}

		// Return the clean-up function
		return cleanUp
	}

	completionUI(type) {
		//this.alertContentGroup.clear(true, true);
		//this.cockpitScene.tabletNotification.setVisible(false)
		playNotificationAnimation(this.cockpitScene, {
			xPos: this.cockpitScene.tabletButton.x + 40 * this.cockpitScene.scaleFactor,
			yPos: this.cockpitScene.tabletButton.y - 40 * this.cockpitScene.scaleFactor
		})
		let endTextString
		let endText02String

		let cockpitScene = this.scene.get("CockpitScene")
		let tabletScene = this.scene.get("TabletScene")
		if (type === "securityCheck") {
			endTextString =
				"[b]Well done![/b]\nYou have earned some fuel"
			endText02String = "Press ‘Done’ to finish."

			//Fuel Gauges
			cockpitScene.duplicateFuelGauge.setTexture("fuelState5")
			cockpitScene.fuelGauge.setTexture("fuelState5")
		}
		if (type === "facialScan") {
			endTextString =
				"[b]Great job![/b]\nFacial recognition has been activated!\nPress 'Done' to finish."
			endText02String = "A confirmation email has been sent to your inbox."

			console.log(
				"Facial Scan Happened and saved minilevel: " + this.savedMiniLevel,
			)
			//Fuel Gauges
			cockpitScene.duplicateFuelGauge.setTexture("fuelFull")
			cockpitScene.fuelGauge.setTexture("fuelFull")
			tabletScene.updateButtonVisibility()

			if (this.currentPlanet === 1 && this.savedMiniLevel === 13) {
				console.log("Check page and show instructions")
				cockpitScene.updateNavigationInstructions()
			}
		}
		if (type === "restartDevice") {
			//cover stuff
			// this.alertsBG02 = this.add.image(this.gameW / 2, this.gameH / 2, 'tabletBG')
			//     .setScale(this.scaleFactor, this.scaleFactor * 1.08).setDepth(10);
			endTextString =
				"[b]Well done![/b]\nYou've shut down and restarted the computer."
			endText02String = "Press ‘Done’ to finish."

			//Fuel Gauges
			cockpitScene.duplicateFuelGauge.setTexture("fuelFull")
			cockpitScene.fuelGauge.setTexture("fuelFull")

			if (this.savedMiniLevel === 13) {
				// displayInstructions(
				// 	cockpitScene,
				// 	"Creature 3 is still waiting! Move on to Tube 1 to care for it!",
				// 	12,
				// 	450,
				// 	60,
				// )
				cockpitScene.creature3Panel()
			}
		}
		if (type === "reconnectDevice") {
			endTextString =
				"[b]Great work![/b]\nYour tablet is connected to the Wi-Fi."
			endText02String = "Press ‘Done’ to go back and re-scan the creature."

			//Fuel Gauges
			cockpitScene.duplicateFuelGauge.setTexture("fuelState4")
			cockpitScene.fuelGauge.setTexture("fuelState4")
		}
		if (type === "robotAlert") {
			endTextString =
				"[b]Nice work![/b]\nYou have earned some fuel too!"
			endText02String = "Press ‘Done’ to finish."

			//Fuel Gauges
			cockpitScene.duplicateFuelGauge.setTexture("fuelState5")
			cockpitScene.fuelGauge.setTexture("fuelState5")
		}
		if (type === "otpAlert") {
			endTextString =
				"[b]Success[/b]\nYou can now use the Help Desk app on your tablet."
			endText02String = "Press ‘Done’ to finish."

			if (this.savedMiniLevel === 16) {
				// displayInstructions(
				// 	cockpitScene,
				// 	"Creature 3 is still waiting! Move on to Tube 1 to care for it!",
				// 	12,
				// 	450,
				// 	60,
				// )
				// destroyInstructions(cockpitScene)
			}


			tabletScene.showHelpDeskFirstTime()

			//Fuel Gauges
			cockpitScene.duplicateFuelGauge.setTexture("fuelFull")
			cockpitScene.fuelGauge.setTexture("fuelFull")
		}

		// Add close button
		this.closeButton02 = this.add
			.image(
				this.appBar.x + 230 * this.scaleFactor,
				this.appBar.y,
				"appCloseButton",
			)
			.setDepth(10)
			.setScale(this.scaleFactor * 0.5)

		this.closeButton02.setInteractive().on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			this.scene.resume("TabletScene")
			this.tabletScene.updateButtonVisibility()
			this.tabletScene.removeUnlockAnim()
			// this.tabletScene.removeNotification()
			// this.cockpitScene.tabletNotification.setVisible(false)
			// if (this.currentPlanet === 2 && this.savedMiniLevel === 5) {
			// 	displayInstructions(
			// 		this.cockpitScene,
			// 		"Press the left and right arrow buttons to look around your ship",
			// 		14,
			// 		500,
			// 		60,
			// 	)
			// }
			this.scene.stop("Alerts")
			this.removeHtmlItems()
		})
		// Show completion UI
		const endText = this.add
			.rexBBCodeText(
				this.alertsBG.x,
				this.alertsBG.y - 40 * this.scaleFactor,
				endTextString,
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 3 * this.scaleFactor,
					fontWeight: "400",
					color: "#11044E",
					align: "center",
					resolution: 2,
				},
			)
			.setDepth(10)
			.setOrigin(0.5)

		const fuelHolder = this.add
			.image(
				this.alertsBG.x,
				this.alertsBG.y + 20 * this.scaleFactor,
				"fuelHolder",
			)
			.setDepth(10)
			.setScale(this.scaleFactor * 0.5)

		if (type === "facialScan") {
			const isMobile =
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
					navigator.userAgent,
				)

			let yOffset
			let fontScale
			let wordWrap
			if (isMobile) {
				yOffset = 3
				wordWrap = 1.1
				fontScale = -7
			} else {
				yOffset = 6
				fontScale = -9
				wordWrap = 1.1
			}

			displayTooltip(this, {
				xPos: fuelHolder.x - 200 * this.scaleFactor,
				yPos: fuelHolder.y,
				type: "dischargeCrit",
				scale: 1.05,
				text: "You've earned fuel!",
				fontScale: fontScale,
				xTextOffset: -5,
				yTextOffset: 2,
				wrapWidth: wordWrap,
			})
		}

		const endText02 = this.add
			.text(
				fuelHolder.x,
				fuelHolder.y + 60 * this.scaleFactor,
				endText02String,
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 3 * this.scaleFactor,
					color: "#11044E",
					align: "center",
					resolution: 2,
				},
			)
			.setDepth(10)
			.setOrigin(0.5)

		// Next button
		const doneButton = this.add
			.image(
				this.appBar.x + 200 * this.scaleFactor,
				this.appBar.y + 260 * this.scaleFactor,
				"saveBtn",
			)
			.setDepth(10)
			.setScale(this.scaleFactor)
			.setAlpha(1)

		const nextText = this.add
			.text(doneButton.x, doneButton.y - 10 * this.scaleFactor, "DONE", {
				fontFamily: "Gluten",
				fontSize: this.fontSize,
				color: "#ffff ",
				align: "left",
				fontStyle: "bold",
				fontWeight: "700",
				resolution: 2,
			})
			.setDepth(10)
			.setOrigin(0.5)

		setTimeout(() => {
			playExplosionAnimation(this, {
				x: fuelHolder.x,
				y: fuelHolder.y,
			})
		}, 1000)

		doneButton.setInteractive().on("pointerdown", () => {
			if (type === "reconnectDevice") {
				this.scene.stop("Alerts")
				this.scene.stop("TabletScene")
				this.scene.stop("TabletUI")
				console.log("Proceed to scanning")
				updateMiniLevel(this.savedMiniLevel)
				handleGameProgressUpdate(cockpitScene)
				this.scene.resume("CockpitScene")
				return
			}
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			//this.alertContentGroup.clear(true, true);
			this.handleAlertCreation()

			destroyTooltip(this)
			//updateMiniLevel(this.savedMiniLevel)
			//handleGameProgressUpdate(cockpitScene)
			fuelHolder.setVisible(false)
			endText02.setVisible(false)
			doneButton.setVisible(false)
			nextText.setVisible(false)
			endText.setVisible(false)
			this.closeButton.setVisible(true)
			if (this.pageIndicators[0]) this.pageIndicators[0].setVisible(false)
			if (this.pageIndicators[1]) this.pageIndicators[1].setVisible(false)
			if (this.pageIndicators[2]) this.pageIndicators[2].setVisible(false)
			return
		})

		// Update page indicator for final state
		if (type != "robotAlert" && type != "otpAlert" && type != "securityCheck") {
			this.pageIndicators[1].setTexture("inactivePageIndicator")
			this.pageIndicators[2].setTexture("activePageIndicator")
		}

		this.closeButton.setVisible(false)
		// Add new elements to group
		this.alertContentGroup.add(endText)
		this.alertContentGroup.add(fuelHolder)
		this.alertContentGroup.add(endText02)

		saveAlertsOpenedLocally(type)
	}

	// Function to check if two arrays have the same values
	arraysEqual(arr1, arr2) {
		if (arr1.length !== arr2.length) return false
		for (let i = 0; i < arr1.length; i++) {
			if (arr1[i] !== arr2[i]) return false
		}
		return true
	}
	createAnimations() {
		if (!this.anims.exists("bio_scan")) {
			this.anims.create({
				key: "bio_scan",
				frames: this.anims.generateFrameNames("bioScan", {
					start: 0,
					end: 60,
					zeroPad: 4,
					prefix: "bioscan_",
					suffix: ".png",
				}),
				frameRate: 15,
			})
		}

		this.scan = this.add
			.sprite(this.gameW / 2, this.gameH / 2, "bioScan", "bioscan_0000.png")
			.setScale(this.scaleFactor * 0.5)

		this.alertContentGroup.add(this.scan)
	}

	createCaptchaElements(clearAlertHolder, nextButton, nextText, imageTag) {
		// Create a 3x3 grid of captcha images
		const gridRows = 3
		const gridCols = 3

		// Scale ALL offsets with your existing scaleFactor
		const initialImageOffsetX = 160 * this.scaleFactor
		const imageOffsetX = 65 * this.scaleFactor
		const imageOffsetY = 65 * this.scaleFactor
		const startX = clearAlertHolder.x + initialImageOffsetX
		const startY = clearAlertHolder.y - 10 * this.scaleFactor

		this.selectedImages = []
		this.selectorIcons = []

		for (let row = 0; row < gridRows; row++) {
			for (let col = 0; col < gridCols; col++) {
				const imageX = startX + col * imageOffsetX
				const imageY = startY + row * imageOffsetY
				let index = row * gridCols + col + 1
				const image = this.add
					.image(imageX, imageY, imageTag + index)
					.setDepth(10)
					.setScale(this.scaleFactor * 0.55)

				image.setInteractive().on("pointerdown", () => {
					playUISound(this, this.soundManager, "Subtle click 1", 0.25)

					const selectedIndex = this.selectedImages.indexOf(index)

					if (selectedIndex === -1) {
						// Image not selected - select it
						let selectedImage = this.add
							.image(image.x, image.y, "captchaSelected")
							.setDepth(15)
							.setScale(this.scaleFactor * 0.55)
						this.selectedImages.push(index)
						this.selectorIcons.push(selectedImage)
					} else {
						// Image already selected - unselect it
						this.selectedImages.splice(selectedIndex, 1)
						this.selectorIcons[selectedIndex].destroy()
						this.selectorIcons.splice(selectedIndex, 1)
					}

					// Enable next button if any images are selected
					nextButton.setAlpha(1)
					nextText.setAlpha(1)
					nextButton.setInteractive()
				})
				this.alertContentGroup.add(image)
			}
		}
	}

	wrongCaptchaElement() {
		playUISound(this, this.soundManager, "Error3", 0.25)
		playErrorAnimation(this, {
			includeTablet: false
		})
		console.log(this.captchasDone)
		let oopsBG = this.add
			.image(this.gameW / 2, this.gameH / 2 + 30 * this.scaleFactor, "oopsBG")
			.setScale(this.scaleFactor * 0.5, this.scaleFactor * 0.52)
			.setDepth(200)

		let oopsImage = this.add
			.image(this.alertsBG.x, this.alertsBG.y, "alertOops")
			.setDepth(200)
			.setScale(this.scaleFactor * 0.5)
		let closeButton = this.add
			.image(
				this.appBar.x + 230 * this.scaleFactor,
				this.appBar.y,
				"appCloseButton",
			)
			.setDepth(200)
			.setScale(this.scaleFactor * 0.5)

		this.closeButton.setVisible(false)

		closeButton.setInteractive().on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			console.log("Close pressed")
			destroyIndicator(this)
			oopsImage.destroy()
			oopsBG.destroy()
			closeButton.destroy()
			this.maxSelection = 0
			this.selectedImages = []
			if (this.selectorIcons) {
				this.selectorIcons.forEach((icon) => icon.destroy())
			}
			if (this.shutPills) {
				this.shutPills.setTexture("alertShutPillsDefault")
			}

			this.closeButton.setVisible(true)
		})
		displayIndicator(
			this,
			this.closeButton.x,
			this.closeButton.y,
			this.scaleFactor * 0.5,
			this.scaleFactor * 0.5,
		)
		displayIndicator(
			this,
			this.closeButton.x,
			this.closeButton.y,
			2,
			"buttonTip",
		)
	}
	removeHtmlItems() {
		if (!this.gameContainer) {
			console.log("HAKUNA")
			return
		}
		if (this.gameContainer.contains(this.otpContainer)) {
			this.gameContainer.removeChild(this.otpContainer)
		}
		if (this.wifiPasswordReference) {
			this.wifiPasswordReference.remove()
		}
	}
}
